/**
 * Frontend error reporting (O3 / Sentry).
 *
 * Kept separate from `analytics.ts` so the two vendors (PostHog / Sentry) stay
 * independent. No-op when `VITE_SENTRY_DSN` is unset (local dev + CI): `init`
 * does nothing, `captureError`/`setErrorUser` are no-ops, and the api client's
 * `reportRequestError` falls back to console-only — behavior identical to today.
 *
 * Privacy: `sendDefaultPii` defaults to false (kept); we attach only the opaque
 * Cognito `sub` via `setErrorUser`, the same id PostHog + the O1 backend logs use.
 */

import * as Sentry from '@sentry/react'
import { env } from '@/lib/env'

let initialized = false

/** No-op when VITE_SENTRY_DSN is unset (local dev + CI). */
export function initErrorReporting(): void {
  if (initialized || !env.VITE_SENTRY_DSN) return
  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    tracesSampleRate: 0.1,
    // sendDefaultPii defaults to false — keep it; we attach only the opaque
    // user id via setErrorUser below.
  })
  initialized = true
}

/** Capture an exception to Sentry. No-op when uninitialized. */
export function captureError(error: unknown, context?: string): void {
  if (!initialized) return
  Sentry.captureException(error, context ? { tags: { context } } : undefined)
}

/** Tag Sentry events with the same opaque user id PostHog/the backend use. */
export function setErrorUser(userId: string | null): void {
  if (initialized) Sentry.setUser(userId ? { id: userId } : null)
}

/**
 * Expected product-flow 4xx responses that are NOT errors — they are normal
 * tier-gate / quota signals (C2 403 `tier_upgrade_required`, B4 429
 * `preview_limit_exhausted`). `reportRequestError` is wired into ky's
 * `beforeError` / refresh-failure hooks and fires on EVERY failed response, so
 * without this filter Sentry would drown in routine tier-gate/quota traffic.
 * We capture only 5xx / network / unexpected errors.
 *
 * Returns true when the error should be SKIPPED (not sent to Sentry).
 */
export function isExpectedProductFlowError(error: unknown): boolean {
  if (error == null || typeof error !== 'object') return false
  const response = (error as { response?: { status?: number } }).response
  const status = response?.status
  // No HTTP response → network/timeout/unexpected: DO capture (not expected).
  if (typeof status !== 'number') return false
  // 5xx is always a real error.
  if (status >= 500) return false
  // Non-4xx (e.g. 3xx) — leave to default handling; treat as not-expected.
  if (status < 400 || status >= 500) return false
  // 4xx: only the known product-flow tier-gate / quota responses are expected.
  // 401 is handled by the client's refresh-then-retry/redirect path, not an
  // error to report; 403 tier-upgrade and 429 quota/preview caps are product flow.
  return status === 401 || status === 403 || status === 429
}
